import type { GradeResult, GradedToken, TokenStatus } from './grade'

/**
 * Word-level grading for alphabetic languages.
 *
 * Aligned by word rather than by character: in German a single mistyped letter
 * should read as one wrong word, not as a cascade of shifted characters. The
 * Mandarin grader aligns per character because a Han character *is* the unit.
 *
 * Two tiers of correctness:
 *   · exact — the word matches outright
 *   · near miss — the right word in the wrong form: a capitalisation slip, a
 *     missing accent, or an ASCII spelling typed on a keyboard without the
 *     right keys
 *
 * Near misses earn credit but are always reported, because in several of these
 * languages the "decoration" is grammar: German Sie and sie are different
 * words, Spanish año and ano are very different nouns. Silently accepting them
 * would teach the wrong thing.
 */

const PUNCT = /[.,!?;:—–"'“”„«»()[\]{}…]/g

/**
 * Fold a word to its bare form for near-miss comparison.
 *
 * Diacritics are stripped generically via Unicode decomposition, so this covers
 * French accents, Spanish tildes and Italian grave accents as readily as German
 * umlauts — adding one of those languages needs no change here. The two
 * German-specific lines handle spellings that are substitutions rather than
 * decorations: ß is not a decorated s, and ue/oe/ae are what people type on a
 * keyboard that has no umlaut key at all.
 */
function fold(word: string): string {
  return word
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
}

function words(text: string): string[] {
  return text
    .replace(PUNCT, ' ')
    .split(/\s+/)
    .map((w) => w.trim())
    .filter(Boolean)
}

/** Exact, near miss, or neither. */
function compare(expected: string, got: string): 'exact' | 'near' | 'no' {
  if (expected === got) return 'exact'
  return fold(expected) === fold(got) ? 'near' : 'no'
}

interface Op {
  kind: 'match' | 'sub' | 'del' | 'ins'
  a: number
  b: number
}

/**
 * Levenshtein alignment over words, with near misses costing less than a real
 * substitution so the aligner prefers pairing "Straße" with "strasse" over
 * treating both as unrelated insert-and-delete.
 */
function align(a: string[], b: string[]): Op[] {
  const n = a.length
  const m = b.length
  const cost: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0))

  for (let i = 0; i <= n; i++) cost[i][0] = i
  for (let j = 0; j <= m; j++) cost[0][j] = j

  const penalty = (x: string, y: string) => {
    const verdict = compare(x, y)
    return verdict === 'exact' ? 0 : verdict === 'near' ? 0.25 : 1
  }

  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      cost[i][j] = Math.min(
        cost[i - 1][j - 1] + penalty(a[i - 1], b[j - 1]),
        cost[i - 1][j] + 1,
        cost[i][j - 1] + 1,
      )
    }
  }

  const ops: Op[] = []
  let i = n
  let j = m
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && cost[i][j] === cost[i - 1][j - 1] + penalty(a[i - 1], b[j - 1])) {
      ops.push({ kind: penalty(a[i - 1], b[j - 1]) === 1 ? 'sub' : 'match', a: i - 1, b: j - 1 })
      i--
      j--
      continue
    }
    if (i > 0 && cost[i][j] === cost[i - 1][j] + 1) {
      ops.push({ kind: 'del', a: i - 1, b: -1 })
      i--
      continue
    }
    ops.push({ kind: 'ins', a: -1, b: j - 1 })
    j--
  }
  return ops.reverse()
}

export function gradeText(target: string, attempt: string): GradeResult {
  const a = words(target)
  const b = words(attempt)
  const ops = align(a, b)

  const tokens: GradedToken[] = ops.map((op) => {
    switch (op.kind) {
      case 'match': {
        const exact = compare(a[op.a], b[op.b]) === 'exact'
        return {
          status: (exact ? 'correct' : 'tone') as TokenStatus,
          expected: a[op.a],
          got: b[op.b],
          expectedTone: 0,
          gotTone: 0,
        }
      }
      case 'sub':
        return {
          status: 'wrong' as TokenStatus,
          expected: a[op.a],
          got: b[op.b],
          expectedTone: 0,
          gotTone: 0,
        }
      case 'del':
        return {
          status: 'missing' as TokenStatus,
          expected: a[op.a],
          got: '',
          expectedTone: 0,
          gotTone: 0,
        }
      default:
        return {
          status: 'extra' as TokenStatus,
          expected: '',
          got: b[op.b],
          expectedTone: 0,
          gotTone: 0,
        }
    }
  })

  const counts: Record<TokenStatus, number> = {
    correct: 0,
    tone: 0,
    wrong: 0,
    missing: 0,
    extra: 0,
  }
  for (const t of tokens) counts[t.status]++

  // A near miss earns most of the mark: the word was known, the form was not.
  const credit = counts.correct + counts.tone * 0.75 - counts.extra * 0.5
  const score = a.length === 0 ? 0 : Math.max(0, Math.min(100, (credit / a.length) * 100))

  const known = counts.correct + counts.tone
  return {
    score: Math.round(score),
    // Reused as "form accuracy" for languages without tones.
    toneAccuracy: known === 0 ? null : Math.round((counts.correct / known) * 100),
    tokens,
    counts,
    targetLength: a.length,
  }
}
